'use client';

import Image from 'next/image';
import Link from 'next/link';
import { Suspense, useEffect } from 'react';
import { useSearchParams } from 'next/navigation';
import { useTranslation } from 'react-i18next';

import { LoginSkeleton } from '@/components/shared/skeletons/LoginSkeleton';
import AuthFooter from '@/components/AuthFooter';
import AppNoticeDialog from '@/components/shared/ui/AppNoticeDialog';

import LoginForm from './components/LoginForm';
import { resetGlobalStore } from '@/lib/logout';
import { useAppDispatch } from '@/hooks/lib/customStore';

const loginReasonBannerMap: Record<string, string> = {
  'session-evicted': 'LoginPage.banner-session-evicted',
  'refresh-invalid': 'LoginPage.banner-refresh-invalid',
  'session-expired': 'LoginPage.banner-session-expired',
};

// Se orquesta el flujo de autenticación delegando LoginForm (usa FormMessage) y se expone AppNoticeDialog para incidentes globales.
export default function LoginPage() {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const searchParams = useSearchParams();

  // Se interpreta reason en querystring para mostrar banners contextuales (session-evicted, refresh-invalid, session-expired).
  const reason = (searchParams?.get('reason') || '').toLowerCase();
  const bannerKey = loginReasonBannerMap[reason];
  const bannerMessage = bannerKey ? t(bannerKey) : '';

  useEffect(() => {
    // Limpiar estado persistido clave al llegar a /auth o /login
    resetGlobalStore({ dispatch });
  }, [dispatch]);

  return (
    <Suspense fallback={<LoginSkeleton />}>
      <div className="grid min-h-svh lg:grid-cols-2">
        <div className="flex flex-col gap-6 p-6 md:p-10 bg-background">
          <div className="flex justify-center gap-2 md:justify-start">
            <Link href="/" className="flex items-center gap-2 font-semibold tracking-tight">
              <Image
                src="/logo.svg"
                alt="Reparoly"
                width={58}
                height={58}
                className="h-12 w-12 rounded-md"
              />
              Reparoly
            </Link>
          </div>
          <div className="flex flex-1 items-center justify-center">
            <div className="w-full max-w-md">
              <div className="mb-6">
                <h1 className="text-2xl font-extrabold text-foreground">
                  {t('LoginPage.title', { defaultValue: 'Inicia sesión para continuar' })}
                </h1>
              </div>
              {bannerMessage && (
                <div className="mb-4 rounded-md border border-yellow-300 bg-yellow-50 text-yellow-900 px-3 py-2 text-sm">
                  {bannerMessage}
                </div>
              )}
              <LoginForm />
              <p className="mt-6 text-sm text-muted-foreground">
                {t('LoginPage.cta-no-account', { defaultValue: '¿No tienes cuenta?' })}{' '}
                <Link href="/signup" className="font-semibold underline">
                  {t('LoginPage.cta-create-account', { defaultValue: 'Crear cuenta' })}
                </Link>
              </p>
            </div>
          </div>
         
          <AuthFooter />
        </div>

        <div className="relative hidden lg:flex items-center overflow-hidden">
          <div className="absolute inset-0 bg-primary">
            <div className="absolute inset-0 bg-[radial-gradient(1200px_500px_at_80%_-10%,_rgba(255,255,255,0.35),_transparent_60%)]" />
            <div className="absolute inset-0 bg-[linear-gradient(135deg,rgba(255,255,255,0.08),transparent_40%)]" />
          </div>

          <div className="relative z-10 w-full px-10 xl:px-16 py-12 text-white flex flex-col gap-10">
            <div className="max-w-xl">
              <h2 className="text-4xl xl:text-5xl font-extrabold leading-tight text-foreground">
                {t('LoginPage.hero-title', { defaultValue: 'Accede y continúa tu trabajo' })}
              </h2>
              <p className="mt-4 text-lg xl:text-xl !text-white/90">
                {t('LoginPage.hero-subtitle', { defaultValue: 'Tu taller, siempre a mano.' })}
              </p>
            </div>

            <div className="mt-2" />
          </div>

          <div className="absolute -top-20 -right-20 size-60 rounded-full bg-white/10 blur-2xl" />
        </div>
      </div>
      <AppNoticeDialog />
    </Suspense>
  );
}
